import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";

const JsonFormatterContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Intro */}
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        JSON Formatter – Beautify and Validate Your JSON Instantly
      </Typography>
      <Typography
        variant="h6"
        component="h2"
        color="text.secondary"
        gutterBottom
      >
        Format, validate, and beautify your JSON with customizable indentation
        and real-time error checking
      </Typography>

      <Typography variant="body1" paragraph>
        Tired of messy, hard-to-read JSON data? Use our JSON Formatter to clean
        up your JSON instantly. With support for custom indentation levels and
        minified output, this tool helps developers and analysts quickly
        structure JSON for debugging, documentation, or collaboration.
      </Typography>
      <Typography variant="body1" paragraph>
        Whether you're pasting API responses or working with configuration
        files, our JSON formatter ensures your data is readable and well-structured
        every time.
      </Typography>

      {/* Features */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Why Use Our JSON Formatter?
      </Typography>
      <ul style={{ textAlign: "left", display: "inline-block" }}>
        <li>✨ Beautify raw JSON into readable and structured format</li>
        <li>🛠️ Validate JSON and catch syntax errors in real time</li>
        <li>📏 Choose indentation style: 2 spaces, 4 spaces, tabs, or minified</li>
        <li>📋 Copy formatted JSON or download it as a file in one click</li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How to Use the JSON Formatter
      </Typography>
      <ol style={{ textAlign: "left", display: "inline-block" }}>
        <li>Paste your JSON data into the input field.</li>
        <li>Select your desired indentation style (2 spaces, 4 spaces, tab, or none).</li>
        <li>Click “Format JSON” to beautify and validate the input.</li>
        <li>Copy the result or download it as a .json file.</li>
      </ol>

      {/* SEO Paragraph */}
      <Typography variant="body1" paragraph mt={4}>
        Our <b>JSON formatter</b> is built for speed and accuracy. It's ideal
        for developers working with APIs, data engineers managing configs, or
        anyone handling JSON manually. Quickly detect syntax issues and
        standardize your formatting across teams.
      </Typography>

      <Typography variant="body1" paragraph>
        Don’t waste time reading unformatted JSON—let our tool clean it up for
        you with a single click. Improve readability and reduce errors in your
        development workflow using our free online <b>JSON Formatter</b>.
      </Typography>

      {/* FAQs */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Frequently Asked Questions (FAQs)
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        💡 What is a JSON formatter?
      </Typography>
      <Typography variant="body2" paragraph>
        A JSON formatter helps you beautify and structure your JSON data,
        making it easier to read, understand, and debug. It can also validate
        your data for common syntax errors.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        🆓 Is the JSON formatter free to use?
      </Typography>
      <Typography variant="body2" paragraph>
        Yes! Free plan users can format JSON using credits, while Paid users can
        enjoy unlimited formatting with no credit deduction.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        🧱 Can I minify JSON instead of beautifying it?
      </Typography>
      <Typography variant="body2" paragraph>
        Absolutely. Just select the “Minified” option from the indentation
        dropdown to compress your JSON into a single line.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        📥 Can I download the formatted JSON?
      </Typography>
      <Typography variant="body2" paragraph>
        Yes, after formatting your JSON, you can download the result as a
        `.json` file or copy it directly to your clipboard.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        🧩 Does the tool support large JSON files?
      </Typography>
      <Typography variant="body2" paragraph>
        Our formatter handles moderately large files efficiently in-browser.
        For very large files, we recommend using a desktop tool or upgrading to
        our API support (for Pro users).
      </Typography>

      {/* CTA */}
      <Box mt={4}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={scrollToTop}
        >
          Format Your JSON Now
        </Button>
      </Box>
    </Container>
  );
};

export default JsonFormatterContent;
